import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef
} from '@angular/core'
import { NavigationEnd, Router } from '@angular/router'
import { TranslateService } from '@ngx-translate/core'
import { LoginService } from 'src/app/modules/login/services/login.service'
import { MenuItem } from './side-nav.models'
import { MenuLevel } from 'src/app/core/models/enums/menu-level'
import { Subject, takeUntil } from 'rxjs'

import { CommonModule } from '@angular/common'
import { RouterModule } from '@angular/router'
import { TranslatePipe } from '@ngx-translate/core'
import { MatExpansionModule } from '@angular/material/expansion'
import { TooltipModule } from 'primeng/tooltip'

@Component({
  selector: 'app-side-nav',
  templateUrl: './side-nav.component.html',
  styleUrls: ['./side-nav.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TranslatePipe,
    MatExpansionModule,
    TooltipModule
  ]
})
export class SideNavComponent implements OnInit, OnDestroy {
  @Input() isCollapsed: boolean = false
  @Input() isMobileView: boolean = false
  @Output() menuItemSelected = new EventEmitter<any>()

  currentLanguage: string = localStorage.getItem('lang') || 'en'
  isAdmin: boolean = false
  menuItems: MenuItem[] = []
  MenuLevel = MenuLevel // Make enum available in template

  footItems: any[] = []

  private destroy$ = new Subject<void>()

  constructor(
    private loginService: LoginService,
    private translateService: TranslateService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.setupLanguageSubscription()
    this.setupRouterSubscription()
    this.initializeComponent()
    // Expansion is driven by item.expanded (no longer by isChildActive in the template),
    // so set the initial state explicitly: the router subscription only fires on FUTURE
    // navigations, which misses a hard refresh / direct load onto a child route.
    this.collapseNonActiveMenuSections()
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  private setupLanguageSubscription(): void {
    this.translateService.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe((lang) => {
        this.currentLanguage = lang.lang
        this.cdr.markForCheck()
      })
  }

  private setupRouterSubscription(): void {
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.resetMenuStates()
        this.collapseNonActiveMenuSections()
        this.cdr.markForCheck()
      }
    })
  }

  private initializeComponent(): void {
    this.isAdmin = this.loginService.isAdmin()
    this.initMenuItems()
    this.adjustMenuForUserRole()
  }

  private adjustMenuForUserRole(): void {
    const dashboardItem = this.findMenuItem('SideNav.Risks')?.children?.[0]
    if (dashboardItem) {
      dashboardItem.url = this.isAdmin ? '/admin/dashboard' : '/home'
    }

    if (!this.isAdmin) {
      const riskMenu = this.findMenuItem('SideNav.Risks')
      if (riskMenu?.children && riskMenu.children.length > 3) {
        riskMenu.children.splice(3, 1)
      }
    }
  }

  private findMenuItem(name: string): MenuItem | undefined {
    return this.menuItems.find((item) => item.name === name)
  }

  private initMenuItems(): void {
    this.menuItems = [
      // Governance
      {
        name: 'SideNav.Governance',
        image: 'governance.svg',
        icon: '',
        url: '',
        isShown: true,
        children: [
          {
            name: 'SideNav.Documents-Managment',
            icon: '',
            url: '/document-managment',
            isShown: true
          },
          {
            name: 'SideNav.Documents-Approvement',
            icon: '',
            url: '/document-approvement',
            isShown: true
          },
          {
            name: 'SideNav.Document-Types',
            icon: '',
            url: '/document-type',
            isShown: true
          },
          {
            name: 'SideNav.Approval-Cycle-Plan',
            icon: '',
            url: '/approval-cycle',
            isShown: true
          },
          {
            name: 'SideNav.Events',
            icon: '',
            url: '/event',
            isShown: true
          },
          {
            name: 'SideNav.Meetings-Management',
            image: '',
            url: '/meetings-management',
            isShown: true
          }
        ]
      },
      // Risks
      {
        name: 'SideNav.Risks',
        icon: 'exclamation-diamond-fill',
        isShown: true,
        children: [
          {
            name: 'SideNav.Dashboard',
            url: '/admin/dashboard',
            isShown: true
          },
          {
            name: 'SideNav.Risks',
            url: '/risks',
            isShown: true
          },
          {
            name: 'SideNav.Treatments',
            url: '/treatments',
            isShown: true
          }
        ]
      },
      // Audits & Inspections
      {
        name: 'SideNav.Audits',
        icon: 'clipboard-check',
        isShown: true,
        children: [
          {
            name: 'SideNav.Engagements',
            url: '/audits',
            isShown: true
          },
          {
            name: 'SideNav.Programmes',
            url: '/audits/programmes',
            isShown: true
          },
          {
            name: 'SideNav.AuditUniverse',
            url: '/audits/universe',
            isShown: true
          },
          {
            name: 'SideNav.ChecklistTemplates',
            url: '/audits/checklist-templates',
            isShown: true
          },
          {
            name: 'SideNav.InspectionSchedules',
            url: '/audits/inspection-schedules',
            isShown: true
          },
          {
            name: 'SideNav.AuditDashboard',
            url: '/audits/dashboard',
            isShown: true
          },
          {
            name: 'SideNav.AuditorCompetence',
            url: '/audits/competence',
            isShown: true
          }
        ]
      },
      // Issue & Incident Management
      {
        name: 'SideNav.IssueIncident',
        icon: 'exclamation-triangle',
        isShown: true,
        children: [
          {
            name: 'SideNav.IssueDashboard',
            url: '/issue-incident/dashboard',
            isShown: true
          },
          { name: 'SideNav.Ncrs', url: '/issue-incident/ncrs', isShown: true },
          {
            name: 'SideNav.Capas',
            url: '/issue-incident/capas',
            isShown: true
          },
          {
            name: 'SideNav.Effectiveness',
            url: '/issue-incident/effectiveness',
            isShown: true
          },
          {
            name: 'SideNav.IssueSettings',
            url: '/issue-incident/settings',
            isShown: true
          }
        ]
      },
      // Compliance
      {
        name: 'SideNav.Compliance',
        image: 'compliance.svg',
        isShown: true,
        children: [
          {
            name: 'SideNav.Dashboard',
            url: '/dashboard',
            isShown: true
          },
          {
            name: 'SideNav.Packages',
            url: '/package',
            isShown: true
          },
          {
            name: 'SideNav.Assessments',
            url: '/assessment',
            isShown: true
          }
        ]
      },
      // Maturity
      {
        name: 'SideNav.Maturity',
        image: 'compliance.svg',
        isShown: true,
        children: [
          {
            name: 'SideNav.Maturity-Dashboard',
            url: '/maturity-dashboard',
            isShown: true
          },
          {
            name: 'SideNav.Maturity-Packages',
            url: '/maturity-package',
            isShown: true
          },
          {
            name: 'SideNav.Maturity-Assessments',
            url: '/maturity-assessment',
            isShown: true
          }
        ]
      },
      // Settings
      {
        name: 'SideNav.Setting-Management',
        icon: 'gear-fill',
        isShown: true,
        url: '',
        children: [
          // Profile Settings
          {
            name: 'SideNav.Entity-Settings',
            icon: '',
            isShown: true,
            children: [
              // Company Profile
              {
                name: 'SideNav.Company-Profile',
                icon: '',
                url: '/company-profile',
                children: [],
                isShown: this.loginService.isAdmin() ? true : false
              },
              // Entity Managements
              {
                name: 'SideNav.Entity-Managements',
                icon: '',
                url: '/settings/entity-managements',
                children: [],
                isShown: this.loginService.isAdmin() ? true : false
              },
              // Employees
              {
                name: 'SideNav.Employees',
                icon: '',
                url: '/settings/employee',
                children: [],
                isShown: this.loginService.isAdmin() ? true : false
              }
            ]
          },
          //  System Settings
          {
            name: 'SideNav.System-Settings',
            icon: '',
            isShown: true,
            children: [
              {
                name: 'SideNav.Notification',
                icon: '',
                url: '/profile-settings',
                children: [],
                isShown: true
              },

              // User Management
              {
                name: 'SideNav.User-Management',
                icon: '',
                isShown: this.loginService.isAdmin() ? true : false,
                url: '/users',
                children: []
              }
            ]
          },
          {
            name: 'SideNav.Risk-Settings',
            icon: '',
            url: '',
            isShown: true,
            children: [
              {
                name: 'SideNav.Risk-Category',
                icon: '',
                url: '/settings/risk-category',
                isShown: true
              },
              {
                name: 'SideNav.Risk-Event-Source',
                icon: '',
                url: '/settings/risk-event-source',
                isShown: true
              },
              {
                name: 'SideNav.Actors',
                icon: '',
                url: '/settings/actor',
                isShown: true
              }
            ]
          },
          {
            name: 'SideNav.Assets-Settings',
            icon: '',
            isShown: true,
            children: [
              {
                name: 'SideNav.Assets',
                icon: '',
                url: '/settings/assets',
                isShown: true
              },
              {
                name: 'SideNav.Assets-Category',
                icon: '',
                url: '/settings/assets-category',
                isShown: true
              },
              {
                name: 'SideNav.Assets-Location',
                icon: '',
                url: '/settings/assets-location',
                isShown: true
              }
            ]
          },
          {
            name: 'SideNav.Threats-Settings',
            icon: '',
            url: '',
            isShown: true,
            children: [
              {
                name: 'SideNav.Threats',
                icon: '',
                url: '/settings/threats',
                isShown: true
              },
              {
                name: 'SideNav.Threats-Category',
                icon: '',
                url: '/settings/threats-category',
                isShown: true
              }
            ]
          },
          {
            name: 'SideNav.Vulnerability-Settings',
            icon: '',
            url: '',
            isShown: true,
            children: [
              {
                name: 'SideNav.Vulnerability',
                icon: '',
                url: '/settings/vulnerability',
                isShown: true
              },
              {
                name: 'SideNav.Vulnerability-Category',
                icon: '',
                url: '/settings/vulnerability-category',
                isShown: true
              }
            ]
          },
          {
            name: 'SideNav.Control-Settings',
            icon: '',
            url: '',
            isShown: true,
            children: [
              {
                name: 'SideNav.Control',
                icon: '',
                url: '/settings/control',
                isShown: true
              },
              {
                name: 'SideNav.Control-Category',
                icon: '',
                url: '/settings/control-category',
                isShown: true
              }
            ]
          }
        ]
      },

      // Help-Center
      {
        name: 'SideNav.Help-Center',
        image: 'help.svg',
        url: '/help',
        isShown: true
      },
      // Logout
      {
        name: 'SideNav.Logout',
        image: 'logout.svg',
        url: '/login',
        isShown: true
      }
    ]
    // Help-Center
    this.footItems = [
      {
        name: 'SideNav.Help-Center',
        image: 'help.svg',
        url: '/help',
        isShown: true
      },
      {
        name: 'SideNav.Logout',
        image: 'logout.svg',
        url: '/login',
        isShown: true
      }
    ]
  }
  /**
   * Handle menu item click for simple links
   */
  onMenuItemClick(item: MenuItem): void {
    if (item.url === '/login') {
      this.handleLogout()
    }

    // Emit event for parent components (like mobile sidenav) to handle
    this.menuItemSelected.emit(item)
  }

  /**
   * Handle parent header click for items with children - navigate and toggle
   */
  onParentHeaderClick(item: MenuItem): void {
    // Check if this is one of the specific menu items that should navigate and toggle
    const navigableParents = [
      'SideNav.Assets',
      'SideNav.Threats',
      'SideNav.Control',
      'SideNav.vulnerability'
    ]

    if (navigableParents.includes(item.name) && item.url) {
      // Navigate to the parent URL
      this.router.navigate([item.url])

      // Also emit the event for consistency
      this.menuItemSelected.emit(item)

      // The expansion will still be handled by the mat-expansion-panel's built-in behavior
      // We don't need to prevent default or stop propagation
    }
  }

  /**
   * Handle hover parent click for collapsed sidebar - navigate and toggle
   */
  onHoverParentClick(item: MenuItem): void {
    // Check if this is one of the specific menu items that should navigate and toggle
    const navigableParents = [
      'SideNav.Assets',
      'SideNav.Threats',
      'SideNav.Control',
      'SideNav.vulnerability'
    ]

    if (navigableParents.includes(item.name) && item.url) {
      // Navigate to the parent URL
      this.router.navigate([item.url])

      // Also emit the event for consistency
      this.menuItemSelected.emit(item)
    }

    // Always toggle the hover expansion
    this.toggleHoverItem(item)
  }

  /**
   * Handle expansion panel toggle with accordion behavior
   */
  onPanelToggle(item: MenuItem, expanded: boolean): void {
    if (expanded) {
      // Collapse all sibling panels when expanding this one
      // This is a user-initiated action, so we collapse siblings regardless of active state
      this.collapseExpandedSiblings(item, true)
    }

    item.expanded = expanded
    this.cdr.markForCheck()
  }

  /**
   * Show hover menu for collapsed sidebar
   */
  showHoverMenu(item: MenuItem, event?: MouseEvent): void {
    this.resetHoverStates()
    item.hovered = true

    // Calculate and set dropdown position
    if (event) {
      const target = event.currentTarget as HTMLElement
      const rect = target.getBoundingClientRect()
      const viewportHeight = window.innerHeight
      if (rect.top > viewportHeight - rect.bottom) {
        // Open upwards
        item.dropdownTop = viewportHeight - rect.top
      } else {
        // Open downwards
        item.dropdownTop = rect.bottom
      }
      item.dropdownLeft =
        this.currentLanguage === 'ar' ? rect.left - 270 : rect.right + 20
    }

    this.cdr.markForCheck()
  }

  /**
   * Hide hover menu for collapsed sidebar
   */
  hideHoverMenu(item: MenuItem): void {
    item.hovered = false
    this.resetHoverExpansion(item)
    this.cdr.markForCheck()
  }

  /**
   * Handle mouse leave from menu item trigger
   */
  onMenuItemMouseLeave(item: MenuItem): void {
    // Add a small delay to allow mouse to move to dropdown
    setTimeout(() => {
      if (!item.dropdownHovered) {
        this.hideHoverMenu(item)
      }
    }, 100)
  }

  /**
   * Handle mouse enter on dropdown
   */
  onDropdownMouseEnter(item: MenuItem): void {
    item.dropdownHovered = true
  }

  /**
   * Handle mouse leave from dropdown
   */
  onDropdownMouseLeave(item: MenuItem): void {
    item.dropdownHovered = false
    this.hideHoverMenu(item)
  }

  /**
   * Toggle hover item expansion in dropdown with accordion behavior
   */
  toggleHoverItem(item: MenuItem): void {
    const wasExpanded = item.hoverExpanded

    // Find the parent container to collapse siblings
    const parentItem = this.findParentMenuItem(item)

    if (parentItem) {
      // Collapse all siblings at the same level
      this.collapseHoverSiblings(parentItem, item)
    } else {
      // If no parent found, collapse all top-level items in the current hovered menu
      const hoveredMenuItem = this.menuItems.find(
        (menuItem) => menuItem.hovered
      )
      if (hoveredMenuItem) {
        this.collapseHoverSiblings(hoveredMenuItem, item)
      }
    }

    // Toggle the current item
    item.hoverExpanded = !wasExpanded
    this.cdr.markForCheck()
  }

  /**
   * Check if any child of the menu item is active
   */
  isChildActive(item: MenuItem): boolean {
    if (!item.children) return false

    return item.children.some((child) => {
      if (child.url && this.router.isActive(child.url, false)) {
        return true
      }
      return this.isChildActive(child)
    })
  }

  /**
   * Reset all menu states
   */
  private resetMenuStates(): void {
    this.resetHoverStates()
  }

  /**
   * Reset hover states for all menu items
   */
  private resetHoverStates(): void {
    this.menuItems.forEach((item) => this.resetItemHoverState(item))
  }

  /**
   * Recursively reset hover state for menu item and its children
   */
  private resetItemHoverState(item: MenuItem): void {
    item.hovered = false
    item.dropdownHovered = false
    this.resetHoverExpansion(item)
    if (item.children) {
      item.children.forEach((child) => this.resetItemHoverState(child))
    }
  }

  /**
   * Reset hover expansion state for menu item and its children
   */
  private resetHoverExpansion(item: MenuItem): void {
    item.hoverExpanded = false
    if (item.children) {
      item.children.forEach((child) => this.resetHoverExpansion(child))
    }
  }

  /**
   * Find the parent menu item that contains the given item
   */
  private findParentMenuItem(targetItem: MenuItem): MenuItem | null {
    for (const menuItem of this.menuItems) {
      const parent = this.findParentInTree(menuItem, targetItem)
      if (parent) {
        return parent
      }
    }
    return null
  }

  /**
   * Recursively search for parent in menu tree
   */
  private findParentInTree(
    currentItem: MenuItem,
    targetItem: MenuItem
  ): MenuItem | null {
    if (currentItem.children) {
      // Check if target is a direct child
      if (currentItem.children.includes(targetItem)) {
        return currentItem
      }

      // Recursively search in children
      for (const child of currentItem.children) {
        const parent = this.findParentInTree(child, targetItem)
        if (parent) {
          return parent
        }
      }
    }
    return null
  }

  /**
   * Collapse all sibling hover items except the specified one
   */
  private collapseHoverSiblings(
    parentItem: MenuItem,
    exceptItem: MenuItem
  ): void {
    if (parentItem.children) {
      parentItem.children.forEach((child) => {
        if (child !== exceptItem) {
          this.resetHoverExpansion(child)
        }
      })
    }
  }

  /**
   * Collapse all sibling expanded panels except the specified one
   */
  private collapseExpandedSiblings(
    exceptItem: MenuItem,
    forceCollapse: boolean = false
  ): void {
    // Find the parent of the current item to collapse its siblings
    const parentItem = this.findParentMenuItem(exceptItem)

    if (parentItem) {
      // Collapse siblings within the parent
      if (parentItem.children) {
        parentItem.children.forEach((child) => {
          if (child !== exceptItem && child.expanded) {
            // If forceCollapse is true, collapse regardless of active state
            // Otherwise, only collapse if no active children
            if (forceCollapse || !this.isChildActive(child)) {
              child.expanded = false
              this.resetExpansionRecursively(child)
            }
          }
        })
      }
    } else {
      // If no parent found, collapse all top-level menu items
      this.menuItems.forEach((menuItem) => {
        if (menuItem !== exceptItem && menuItem.expanded) {
          // If forceCollapse is true, collapse regardless of active state
          // Otherwise, only collapse if no active children
          if (forceCollapse || !this.isChildActive(menuItem)) {
            menuItem.expanded = false
            this.resetExpansionRecursively(menuItem)
          }
        }
      })
    }
  }

  /**
   * Recursively reset expansion state for all children
   */
  private resetExpansionRecursively(item: MenuItem): void {
    item.expanded = false
    if (item.children) {
      item.children.forEach((child) => this.resetExpansionRecursively(child))
    }
  }

  /**
   * Collapse all menu sections that don't contain the currently active route
   */
  private collapseNonActiveMenuSections(): void {
    this.menuItems.forEach((menuItem) => {
      this.collapseNonActiveRecursively(menuItem)
    })
  }

  /**
   * Recursively collapse menu items that don't contain the active route
   */
  private collapseNonActiveRecursively(item: MenuItem): void {
    if (item.children && item.children.length > 0) {
      // Check if this item or any of its children contain the active route
      const hasActiveChild = this.isChildActive(item)

      if (!hasActiveChild && item.expanded) {
        // This section doesn't contain the active route, so collapse it
        item.expanded = false
        this.resetExpansionRecursively(item)
      } else if (hasActiveChild) {
        // This section contains the active route — auto-expand it on navigation.
        // Expansion is now driven solely by item.expanded (not isChildActive), so
        // the user can still collapse an active section and have it stay collapsed
        // until they navigate to another route inside it.
        item.expanded = true
        item.children.forEach((child) => {
          this.collapseNonActiveRecursively(child)
        })
      }
    }
  }

  /**
   * Handle logout functionality
   */
  private handleLogout(): void {
    // Delegate to LoginService so the server-side HttpOnly auth cookie is cleared too.
    this.loginService.logout()
  }
}
